import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import AdminLayout from "./AdminLayout";
import { getBrandTeams } from "../../services/brandTeamService";
import { getRiders } from "../../services/riderService";
import { getSeries, getSports } from "../../services/series";
import {
	getFeaturedByType,
	updateFeaturedByType,
} from "../../services/featuredService";
import { BrandTeam, Rider, Series, Sport } from "../../types/model";

const getRiderLabel = (rider: Rider) =>
	`${rider.last_name || ""}, ${rider.first_name || ""}`.trim();

const AdminFeaturedPage = () => {
	const [riders, setRiders] = useState<Rider[]>([]);
	const [brandTeams, setBrandTeams] = useState<BrandTeam[]>([]);
	const [sports, setSports] = useState<Sport[]>([]);
	const [series, setSeries] = useState<Series[]>([]);
	const [riderId, setRiderId] = useState("");
	const [brandTeamId, setBrandTeamId] = useState("");
	const [riderDescription, setRiderDescription] = useState("");
	const [brandTeamDescription, setBrandTeamDescription] = useState("");
	const [selectedSportId, setSelectedSportId] = useState("");
	const [selectedSeriesId, setSelectedSeriesId] = useState("");
	const [riderSearch, setRiderSearch] = useState("");
	const [brandTeamSearch, setBrandTeamSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [savingRider, setSavingRider] = useState(false);
	const [savingBrandTeam, setSavingBrandTeam] = useState(false);

	const activeBrandTeams = useMemo(
		() =>
			brandTeams
				.filter((team) => team.isActive !== false)
				.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
		[brandTeams],
	);

	const activeRiders = useMemo(
		() =>
			riders
				.filter(
					(rider) =>
						(rider.status ? String(rider.status) : "")
							.trim()
							.toLowerCase() === "active",
				)
				.sort((a, b) => getRiderLabel(a).localeCompare(getRiderLabel(b))),
		[riders],
	);

	const riderMatchesSportSeriesFilters = useCallback(
		(rider: Rider) => {
			const riderSports = Array.isArray(rider.sports) ? rider.sports : [];
			const riderSeries = Array.isArray(rider.series) ? rider.series : [];
			const matchesSport = selectedSportId
				? riderSports.includes(selectedSportId)
				: true;
			const matchesSeries = selectedSeriesId
				? riderSeries.includes(selectedSeriesId)
				: true;
			return matchesSport && matchesSeries;
		},
		[selectedSportId, selectedSeriesId],
	);

	const filteredRiders = useMemo(() => {
		const normalizedSearch = riderSearch.trim().toLowerCase();
		return activeRiders.filter((rider) => {
			if (!riderMatchesSportSeriesFilters(rider)) {
				return false;
			}

			if (!normalizedSearch) {
				return true;
			}

			const fullName = `${rider.first_name || ""} ${rider.last_name || ""}`
				.trim()
				.toLowerCase();
			const team = String(rider.team || "").toLowerCase();
			const country = String(rider.country || "").toLowerCase();
			return (
				fullName.includes(normalizedSearch) ||
				team.includes(normalizedSearch) ||
				country.includes(normalizedSearch)
			);
		});
	}, [activeRiders, riderMatchesSportSeriesFilters, riderSearch]);

	const filteredBrandTeams = useMemo(() => {
		const normalizedSearch = brandTeamSearch.trim().toLowerCase();
		const hasSportSeriesFilters = Boolean(selectedSportId || selectedSeriesId);

		const filteredRidersForTeam = hasSportSeriesFilters
			? activeRiders.filter((rider) => riderMatchesSportSeriesFilters(rider))
			: activeRiders;

		const riderTeamNames = new Set(
			filteredRidersForTeam
				.map((rider) => String(rider.team || "").trim().toLowerCase())
				.filter(Boolean),
		);

		const riderTeamShortNames = new Set(
			filteredRidersForTeam
				.map((rider) => String(rider.team_short || "").trim().toLowerCase())
				.filter(Boolean),
		);

		return activeBrandTeams.filter((team) => {
			const teamName = String(team.name || "").toLowerCase();
			const teamShortName = String(team.shortName || "").toLowerCase();

			const matchesSportSeries = hasSportSeriesFilters
				? riderTeamNames.has(teamName) ||
				  riderTeamShortNames.has(teamShortName) ||
				  riderTeamNames.has(teamShortName) ||
				  riderTeamShortNames.has(teamName)
				: true;

			if (!matchesSportSeries) {
				return false;
			}

			if (!normalizedSearch) {
				return true;
			}

			return (
				teamName.includes(normalizedSearch) ||
				teamShortName.includes(normalizedSearch)
			);
		});
	}, [
		activeBrandTeams,
		activeRiders,
		brandTeamSearch,
		selectedSportId,
		selectedSeriesId,
		riderMatchesSportSeriesFilters,
	]);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const [ridersRes, teamsRes, sportsRes, seriesRes] = await Promise.all([
				getRiders({ status: "active" }),
				getBrandTeams(),
				getSports(),
				getSeries(),
			]);

			setRiders(Array.isArray(ridersRes?.data) ? ridersRes.data : []);
			setBrandTeams(Array.isArray(teamsRes?.data) ? teamsRes.data : []);
			const sportsData = Array.isArray(sportsRes?.data)
				? [...sportsRes.data].sort((a, b) =>
						(a?.name || "").localeCompare(b?.name || ""),
				  )
				: [];
			const seriesData = Array.isArray(seriesRes?.data)
				? [...seriesRes.data].sort((a, b) =>
						(a?.name || "").localeCompare(b?.name || ""),
				  )
				: [];
			setSports(sportsData);
			setSeries(seriesData);
			setSelectedSportId((current) => current || sportsData[0]?._id || "");
			setSelectedSeriesId((current) => current || seriesData[0]?._id || "");
		} catch (error) {
			console.error("Failed to fetch featured manager data:", error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchData();
	}, [fetchData]);

	useEffect(() => {
		const fetchFeaturedByContext = async () => {
			if (!selectedSportId || !selectedSeriesId) {
				setRiderId("");
				setRiderDescription("");
				setBrandTeamId("");
				setBrandTeamDescription("");
				return;
			}

			try {
				setLoading(true);
				const [featuredRiderRes, featuredBrandTeamRes] = await Promise.all([
					getFeaturedByType("rider", {
						sportId: selectedSportId,
						seriesId: selectedSeriesId,
					}),
					getFeaturedByType("brandTeam", {
						sportId: selectedSportId,
						seriesId: selectedSeriesId,
					}),
				]);

				setRiderId(featuredRiderRes?.data?.rider?._id || "");
				setRiderDescription(featuredRiderRes?.data?.description || "");
				setBrandTeamId(featuredBrandTeamRes?.data?.brandTeam?._id || "");
				setBrandTeamDescription(featuredBrandTeamRes?.data?.description || "");
			} catch (error) {
				console.error(
					"Failed to fetch featured manager context configuration:",
					error,
				);
			} finally {
				setLoading(false);
			}
		};

		void fetchFeaturedByContext();
	}, [selectedSportId, selectedSeriesId]);

	const saveFeaturedRider = async () => {
		if (!selectedSportId || !selectedSeriesId) {
			alert("Please choose a sport and series first.");
			return;
		}

		if (!riderId) {
			alert("Please choose a featured rider.");
			return;
		}

		try {
			setSavingRider(true);
			await updateFeaturedByType("rider", {
				sportId: selectedSportId,
				seriesId: selectedSeriesId,
				riderId,
				description: riderDescription,
			});
			alert("Featured rider updated.");
		} catch (error: any) {
			console.error("Failed to update featured rider:", error);
			alert(error?.response?.data?.error || "Failed to update featured rider.");
		} finally {
			setSavingRider(false);
		}
	};

	const saveFeaturedBrandTeam = async () => {
		if (!selectedSportId || !selectedSeriesId) {
			alert("Please choose a sport and series first.");
			return;
		}

		if (!brandTeamId) {
			alert("Please choose a featured brand team.");
			return;
		}

		try {
			setSavingBrandTeam(true);
			await updateFeaturedByType("brandTeam", {
				sportId: selectedSportId,
				seriesId: selectedSeriesId,
				brandTeamId,
				description: brandTeamDescription,
			});
			alert("Featured brand team updated.");
		} catch (error: any) {
			console.error("Failed to update featured brand team:", error);
			alert(
				error?.response?.data?.error || "Failed to update featured brand team.",
			);
		} finally {
			setSavingBrandTeam(false);
		}
	};

	return (
		<AdminLayout>
			<Container className="p-4">
				<h2 className="mb-4">Featured Manager</h2>
				<Card className="mb-4">
					<Card.Header className="fw-semibold">
						Featured Context and Search Filters
					</Card.Header>
					<Card.Body>
						<Row className="g-3">
							<Col md={4}>
								<Form.Group controlId="featuredFilterSport">
									<Form.Label>Sport</Form.Label>
									<Form.Select
										value={selectedSportId}
										onChange={(event) => setSelectedSportId(event.target.value)}
										disabled={loading}
									>
										<option value="">Select Sport</option>
										{sports.map((sport) => (
											<option key={sport._id} value={sport._id}>
												{sport.name}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>
							<Col md={4}>
								<Form.Group controlId="featuredFilterSeries">
									<Form.Label>Series</Form.Label>
									<Form.Select
										value={selectedSeriesId}
										onChange={(event) => setSelectedSeriesId(event.target.value)}
										disabled={loading}
									>
										<option value="">Select Series</option>
										{series.map((item) => (
											<option key={item._id} value={item._id}>
												{item.name}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>
						</Row>
					</Card.Body>
				</Card>
				<Row className="g-4">
					<Col lg={6}>
						<Card className="h-100">
							<Card.Header className="fw-semibold">Featured Rider</Card.Header>
							<Card.Body>
								<Form.Group className="mb-3" controlId="featuredRiderSearch">
									<Form.Label>Search Rider</Form.Label>
									<Form.Control
										type="text"
										value={riderSearch}
										onChange={(event) => setRiderSearch(event.target.value)}
										placeholder="Search by name, team, or country"
										disabled={loading || savingRider}
									/>
								</Form.Group>
								<Form.Group className="mb-3" controlId="featuredRiderSelect">
									<Form.Label>Rider</Form.Label>
									<Form.Select
										value={riderId}
										onChange={(event) => setRiderId(event.target.value)}
										disabled={loading || savingRider}
									>
										<option value="">Select Rider</option>
										{filteredRiders.map((rider) => (
											<option key={rider._id} value={rider._id}>
												{getRiderLabel(rider)}
											</option>
										))}
									</Form.Select>
								</Form.Group>
								<Form.Group className="mb-3" controlId="featuredRiderDescription">
									<Form.Label>Description</Form.Label>
									<Form.Control
										as="textarea"
										rows={3}
										value={riderDescription}
										onChange={(event) => setRiderDescription(event.target.value)}
										placeholder="Enter featured rider description"
										disabled={loading || savingRider}
									/>
								</Form.Group>
								<Button
									variant="primary"
									onClick={saveFeaturedRider}
									disabled={loading || savingRider}
								>
									{savingRider ? "Saving..." : "Save Featured Rider"}
								</Button>
							</Card.Body>
						</Card>
					</Col>

					<Col lg={6}>
						<Card className="h-100">
							<Card.Header className="fw-semibold">Featured Brand Team</Card.Header>
							<Card.Body>
								<Form.Group className="mb-3" controlId="featuredBrandSearch">
									<Form.Label>Search Brand Team</Form.Label>
									<Form.Control
										type="text"
										value={brandTeamSearch}
										onChange={(event) =>
											setBrandTeamSearch(event.target.value)
										}
										placeholder="Search by team name"
										disabled={loading || savingBrandTeam}
									/>
								</Form.Group>
								<Form.Group className="mb-3" controlId="featuredBrandTeamSelect">
									<Form.Label>Brand Team</Form.Label>
									<Form.Select
										value={brandTeamId}
										onChange={(event) => setBrandTeamId(event.target.value)}
										disabled={loading || savingBrandTeam}
									>
										<option value="">Select Brand Team</option>
										{filteredBrandTeams.map((team) => (
											<option key={team._id} value={team._id}>
												{team.name}
											</option>
										))}
									</Form.Select>
								</Form.Group>
								<Form.Group className="mb-3" controlId="featuredBrandDescription">
									<Form.Label>Description</Form.Label>
									<Form.Control
										as="textarea"
										rows={3}
										value={brandTeamDescription}
										onChange={(event) =>
											setBrandTeamDescription(event.target.value)
										}
										placeholder="Enter featured team description"
										disabled={loading || savingBrandTeam}
									/>
								</Form.Group>
								<Button
									variant="primary"
									onClick={saveFeaturedBrandTeam}
									disabled={loading || savingBrandTeam}
								>
									{savingBrandTeam ? "Saving..." : "Save Featured Team"}
								</Button>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
		</AdminLayout>
	);
};

export default AdminFeaturedPage;
